import { useLocale } from "next-intl";
import { createNavigation } from "next-intl/navigation";
import { NavigateOptions } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { useRouter as useToploaderRouter } from "nextjs-toploader/app";
import { UrlObject } from "node:url";
import React from "react";
import { locales, routing } from "../i18n/routing";

type NavigateOptionsWithLocale = Partial<NavigateOptions> & {
  locale?: string;
};

const {
  Link: NavigationLink,
  redirect,
  usePathname,
  useRouter: useIntlRouter,
} = createNavigation(routing);

export { redirect, usePathname };

export const Link = React.forwardRef<
  typeof NavigationLink,
  {
    children?: React.ReactNode;
    href: string | UrlObject;
    style?: React.CSSProperties;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    prefetch?: boolean;
  }
>(({ children, ...props }, ref) => {
  const locale = useLocale().toLocaleLowerCase();
  return (
    <NavigationLink prefetch={false} {...props} locale={locale}>
      {children}
    </NavigationLink>
  );
});
Link.displayName = "Link";

const cleanHref = (href: string) => {
  const parts = href.split("/");
  if (locales.includes(parts[1])) {
    parts.splice(1, 1);
  }
  return parts.join("/");
};

/**
 * Custom router hook that merges functionality of next-intl router and nextjs-toploader.
 *
 * We need both because:
 * - next-intl handles proper i18n routing and translation paths
 * - nextjs-toploader provides loading indicator during navigation
 *
 */
export function useRouter() {
  const intlRouter = useIntlRouter();
  const toploaderRouter = useToploaderRouter();
  const locale = useLocale().toLocaleLowerCase();

  return {
    ...intlRouter,
    push: async (href: string, options?: NavigateOptionsWithLocale) => {
      const clearHref = cleanHref(href);
      await Promise.all([
        toploaderRouter.push(clearHref, options),
        intlRouter.push(clearHref, { locale, ...options }),
      ]);
    },
    replace: async (href: string, options?: NavigateOptionsWithLocale) => {
      const clearHref = cleanHref(href);
      await Promise.all([
        toploaderRouter.replace(clearHref, options),
        intlRouter.replace(clearHref, { locale, ...options }),
      ]);
    },
    refresh: async () => {
      await Promise.all([toploaderRouter.refresh(), intlRouter.refresh()]);
    },
    back: async () => {
      await Promise.all([toploaderRouter.back(), intlRouter.back()]);
    },
    forward: async () => {
      await Promise.all([toploaderRouter.forward(), intlRouter.forward()]);
    },
  };
}
